import React, { useEffect, useRef, useState } from 'react';
import { StoryPage } from './StoryPage.js';

const ASCII_LOGO = [
    '  ____ ___ ____ __  __ _   _ _   _ ____  ',
    ' / ___|_ _/ ___|  \\/  | | | | \\ | |  _ \\ ',
    ' \\___ \\| | |  _| |\\/| | | | |  \\| | | | |',
    '  ___) | | |_| | |  | | |_| | |\\  | |_| |',
    ' |____/___\\____|_|  |_|\\___/|_| \\_|____/'
].join('\n');

/**
 * Cinematic welcome sequence.
 * Phase flow: quotes → logo → (story | exit)
 */
export function SigmundWelcome({ onContinue }) {
    const [phase, setPhase] = useState('quotes');       // quotes | quotes-exit | logo | story
    const audioRef = useRef(null);

    // Preload audio immediately so it's ready when logo phase starts
    useEffect(() => {
        try {
            const audio = new Audio(process.env.PUBLIC_URL + '/hello-world.mov');
            audio.volume = 0.6;
            audio.preload = 'auto';
            audioRef.current = audio;
        } catch (_) { }
    }, []);

    // Phase 1 → Phase 2 transition (8s for quotes, then 1s fade-out)
    useEffect(() => {
        if (phase !== 'quotes') return;
        const timer = setTimeout(() => setPhase('quotes-exit'), 8000);
        return () => clearTimeout(timer);
    }, [phase]);

    // Fade-out the quotes then switch to logo
    useEffect(() => {
        if (phase !== 'quotes-exit') return;
        const timer = setTimeout(() => setPhase('logo'), 1000);
        return () => clearTimeout(timer);
    }, [phase]);

    // Play preloaded audio with a gentle fade-in so it doesn't startle
    useEffect(() => {
        if (phase !== 'logo') return;
        if (!audioRef.current) return;

        const audio = audioRef.current;
        audio.volume = 0;
        audio.play().catch(() => { });

        // Ramp up to 0.35 over 3 seconds (60 steps × 50ms)
        const TARGET = 0.35;
        const STEPS = 60;
        const INTERVAL = 50;
        const increment = TARGET / STEPS;
        let step = 0;
        const fade = setInterval(() => {
            step++;
            audio.volume = Math.min(TARGET, parseFloat((audio.volume + increment).toFixed(4)));
            if (step >= STEPS) clearInterval(fade);
        }, INTERVAL);

        return () => clearInterval(fade);
    }, [phase]);

    // Stop audio on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    function handleContinue() {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        onContinue();
    }

    // ── Story sub-page ──
    if (phase === 'story') {
        return React.createElement(StoryPage, {
            onContinue: handleContinue
        });
    }

    // ── Quotes phase ──
    if (phase === 'quotes' || phase === 'quotes-exit') {
        return React.createElement(
            'div',
            { className: 'welcome-screen' },
            React.createElement(
                'div',
                {
                    className: 'welcome-quotes' + (phase === 'quotes-exit' ? ' welcome-quotes--exiting' : '')
                },
                React.createElement(
                    'p',
                    { className: 'welcome-quote-line' },
                    'One day, in retrospect...'
                ),
                React.createElement(
                    'p',
                    { className: 'welcome-quote-line welcome-quote-line--second' },
                    'The years of struggle will strike you as the most beautiful.'
                ),
                React.createElement(
                    'p',
                    { className: 'welcome-quote-line welcome-quote-line--third' },
                    '- Sigmund Freud'
                )
            )
        );
    }

    // ── Logo phase ──
    return React.createElement(
        'div',
        { className: 'welcome-screen' },
        React.createElement(
            'div',
            { className: 'welcome-logo-phase' },
            React.createElement('pre', { className: 'welcome-ascii-logo' }, ASCII_LOGO),
            React.createElement(
                'div',
                { className: 'welcome-buttons' },
                React.createElement(
                    'button',
                    {
                        className: 'welcome-btn',
                        type: 'button',
                        onClick: () => setPhase('story')
                    },
                    '[ read story ]'
                ),
                React.createElement(
                    'button',
                    {
                        className: 'welcome-btn',
                        type: 'button',
                        onClick: handleContinue
                    },
                    '[ use sigmund ]'
                )
            )
        )
    );
}
